import { FC, useCallback, useMemo, useState } from "react";
import { Category, Product, Promotion } from "@utilities/types";
import { format, formatDistanceToNow, isPast, isFuture } from "date-fns";
import { fr } from "date-fns/locale";
import { Button, Icon, Modal } from "@components";
import { AddPromotion } from "./addPromotion";
import { AddPromoCode } from "./AddPromoCode";
import { PromotionTargetTypeEnum } from "@utilities/enums";

interface Props {
    promotion: Promotion;
    categories?: Category[];
    products?: Product[];
    onEdit: (promotion: Partial<Promotion>) => Promise<void> | void;
    onDelete: (id: string) => void;
    onToggleActive?: (promotion: Promotion) => void;
}

type PromotionStatus = 'active' | 'inactive' | 'expired' | 'upcoming' | 'archived';

/**
 * Détermine si une promotion est un code promo client ou une campagne
 * Code promo: a un code MAIS pas de nom (ou nom vide) ET targetType PRODUCTS sans produits sélectionnés
 * Campagne: a un nom ET (targetType ALL/CATEGORIES ou PRODUCTS avec des produits sélectionnés)
 */
const isPromoCode = (promotion: Promotion): boolean => {
    // Si pas de code, c'est forcément une campagne
    if (!promotion.code) return false;

    // Si a un code mais pas de nom → c'est un code promo
    // Si a un code ET un nom → c'est une campagne avec code optionnel
    const hasName = promotion.name && promotion.name.trim().length > 0;

    if (!hasName) {
        // Pas de nom = code promo client
        return true;
    }

    // A un nom ET un code: vérifie le targetType
    // Si targetType est PRODUCTS sans produits sélectionnés → code promo
    // Sinon → campagne
    return promotion.targetType === PromotionTargetTypeEnum.PRODUCTS &&
        (!promotion.targetProducts || promotion.targetProducts.length === 0);
};

export const PromotionCard: FC<Props> = ({
    promotion,
    categories = [],
    products = [],
    onEdit,
    onDelete,
    onToggleActive,
}) => {

    const [editPromotionModal, setEditPromotionModal] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    const onToggleEditPromotionModal = useCallback(() => {
        setEditPromotionModal((prev) => !prev);
    }, []);

    const handleEditPromotion = useCallback(async (promotionData: Partial<Promotion>) => {
        await onEdit({
            ...promotion,
            ...promotionData,
        });
        setEditPromotionModal(false);
    }, [onEdit, promotion]);

    const handleDeleteConfirm = useCallback(() => {
        onDelete(promotion._id!);
        setShowDeleteConfirm(false);
    }, [onDelete, promotion._id]);

    // Calcul du statut de la promotion
    const promotionStatus = useMemo((): PromotionStatus => {
        if (promotion.isArchived) return 'archived';

        const now = new Date();
        const startDate = new Date(promotion.startDate);
        const endDate = new Date(promotion.endDate);

        if (isPast(endDate)) return 'expired';
        if (isFuture(startDate)) return 'upcoming';
        if (!promotion.isActive) return 'inactive';

        return 'active';
    }, [promotion]);

    // Configuration du badge de statut
    const statusConfig = useMemo(() => {
        switch (promotionStatus) {
            case 'active':
                return {
                    label: 'Active',
                    color: 'bg-green-100 text-green-800 border-green-200',
                    icon: 'CheckCircle',
                };
            case 'inactive':
                return {
                    label: 'Désactivée',
                    color: 'bg-gray-100 text-gray-800 border-gray-200',
                    icon: 'PauseCircle',
                };
            case 'expired':
                return {
                    label: 'Expirée',
                    color: 'bg-red-100 text-red-800 border-red-200',
                    icon: 'Clock',
                };
            case 'upcoming':
                return {
                    label: 'À venir',
                    color: 'bg-blue-100 text-blue-800 border-blue-200',
                    icon: 'Calendar',
                };
            case 'archived':
                return {
                    label: 'Archivée',
                    color: 'bg-orange-100 text-orange-800 border-orange-200',
                    icon: 'Archive',
                };
        }
    }, [promotionStatus]);

    // Texte relatif pour les dates
    const dateInfo = useMemo(() => {
        const now = new Date();
        const startDate = new Date(promotion.startDate);
        const endDate = new Date(promotion.endDate);

        if (promotionStatus === 'upcoming') {
            return `Commence ${formatDistanceToNow(startDate, { addSuffix: true, locale: fr })}`;
        }
        if (promotionStatus === 'active') {
            return `Expire ${formatDistanceToNow(endDate, { addSuffix: true, locale: fr })}`;
        }
        if (promotionStatus === 'expired') {
            return `Expirée ${formatDistanceToNow(endDate, { addSuffix: true, locale: fr })}`;
        }
        return null;
    }, [promotion, promotionStatus]);

    return (
        <>
            <div className={`border rounded-xl shadow-sm bg-white overflow-hidden transition-all hover:shadow-md ${promotionStatus === 'active' ? 'border-green-200' :
                promotionStatus === 'archived' ? 'border-orange-200 opacity-75' :
                    'border-gray-200'
                }`}>
                {/* Badge Type (Campagne ou Code promo) */}
                <div className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${isPromoCode(promotion)
                    ? 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border-b border-amber-200'
                    : 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-800 border-b border-purple-200'
                    }`}>
                    <Icon
                        iconName={isPromoCode(promotion) ? "Tag" : "Megaphone"}
                        size={14}
                    />
                    {isPromoCode(promotion) ? 'Code promo client' : 'Campagne promotionnelle'}
                </div>

                {/* Header avec badge de statut */}
                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${promotion.type === "percentage" ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                            <span className={`text-lg font-bold ${promotion.type === "percentage" ? 'text-purple-600' : 'text-blue-600'
                                }`}>
                                {promotion.type === "percentage" ? '%' : '₣'}
                            </span>
                        </div>
                        <div>
                            <p className="font-semibold text-gray-800">
                                {promotion.name || promotion.code || "Sans nom"}
                            </p>
                            <p className="text-sm text-gray-500">
                                {promotion.type === "percentage"
                                    ? `${promotion.value}% de réduction`
                                    : `${promotion.value.toLocaleString()} Fcfa de réduction`
                                }
                                {promotion.code && promotion.name && (
                                    <span className="ml-2 text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                        Code: {promotion.code}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${statusConfig.color}`}>
                        <Icon iconName={statusConfig.icon as any} size={12} />
                        {statusConfig.label}
                    </div>
                </div>

                {/* Corps de la carte */}
                <div className="px-4 py-3 space-y-3">
                    {/* Info de date relative */}
                    {dateInfo && (
                        <div className={`text-sm font-medium ${promotionStatus === 'active' ? 'text-green-600' :
                            promotionStatus === 'upcoming' ? 'text-blue-600' :
                                'text-gray-500'
                            }`}>
                            <Icon iconName="Clock" size={14} className="inline mr-1" />
                            {dateInfo}
                        </div>
                    )}

                    {/* Dates détaillées */}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                            <Icon iconName="Calendar" size={14} className="text-gray-400" />
                            <span>Du {format(new Date(promotion.startDate), "dd MMM yyyy", { locale: fr })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Icon iconName="CalendarCheck" size={14} className="text-gray-400" />
                            <span>Au {format(new Date(promotion.endDate), "dd MMM yyyy", { locale: fr })}</span>
                        </div>
                    </div>

                    {/* Type d'usage */}
                    <div className="flex flex-wrap gap-2">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${promotion.isSingleUse
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-purple-50 text-purple-700'
                            }`}>
                            <Icon iconName={promotion.isSingleUse ? "Ticket" : "Repeat"} size={12} />
                            {promotion.isSingleUse ? "Usage unique" : "Multi-usage"}
                        </div>

                        {/* Badge de ciblage */}
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${promotion.targetType === PromotionTargetTypeEnum.ALL
                            ? 'bg-green-50 text-green-700'
                            : promotion.targetType === PromotionTargetTypeEnum.CATEGORIES
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-orange-50 text-orange-700'
                            }`}>
                            <Icon
                                iconName={
                                    promotion.targetType === PromotionTargetTypeEnum.ALL
                                        ? "Globe"
                                        : promotion.targetType === PromotionTargetTypeEnum.CATEGORIES
                                            ? "FolderOpen"
                                            : "Package"
                                }
                                size={12}
                            />
                            {promotion.targetType === PromotionTargetTypeEnum.ALL
                                ? "Tous les produits"
                                : promotion.targetType === PromotionTargetTypeEnum.CATEGORIES
                                    ? `${promotion.targetCategories?.length || 0} catégorie(s)`
                                    : promotion.code
                                        ? "Code promo"
                                        : `${promotion.targetProducts?.length || 0} produit(s)`
                            }
                        </div>
                    </div>

                    {/* Note */}
                    {promotion.note && (
                        <p className="text-sm text-gray-500 italic border-l-2 border-gray-200 pl-2">
                            {promotion.note}
                        </p>
                    )}

                    {/* Toggle d'activation pour promotions non archivées et non expirées */}
                    {onToggleActive && !promotion.isArchived && promotionStatus !== 'expired' && (
                        <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-sm text-gray-600">Activer la promotion</span>
                            <input
                                type="checkbox"
                                className="toggle toggle-success toggle-sm"
                                checked={promotion.isActive}
                                onChange={() => onToggleActive(promotion)}
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                {!promotion.isArchived && (
                    <div className="px-4 py-3 bg-gray-50 border-t flex justify-end gap-2">
                        <Button
                            variant="error"
                            className="btn-sm"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <Icon iconName="Trash2" size={16} />
                        </Button>

                        <Button
                            variant="info"
                            className="btn-sm"
                            onClick={onToggleEditPromotionModal}
                        >
                            <Icon iconName="Pencil" size={16} />
                            <span className="hidden sm:inline ml-1">Modifier</span>
                        </Button>
                    </div>
                )}
            </div>

            {/* Modal de modification */}
            <Modal
                isOpen={editPromotionModal}
                onClose={onToggleEditPromotionModal}
                title={isPromoCode(promotion) ? "Modifier le code promo" : "Modifier la campagne"}
            >
                {isPromoCode(promotion) ? (
                    <AddPromoCode
                        promotion={promotion}
                        onCancel={onToggleEditPromotionModal}
                        onCreatePromotion={handleEditPromotion}
                    />
                ) : (
                    <AddPromotion
                        promotion={promotion}
                        categories={categories}
                        products={products}
                        onCancel={onToggleEditPromotionModal}
                        onCreatePromotion={handleEditPromotion}
                    />
                )}
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Confirmer la suppression"
            >
                <div className="space-y-4">
                    <p className="text-gray-600">
                        Êtes-vous sûr de vouloir supprimer la promotion
                        <strong className="text-primary"> {promotion.code || "Sans Code"}</strong> ?
                    </p>
                    <p className="text-sm text-gray-500">
                        Cette action est irréversible.
                    </p>
                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            variant="secondary"
                            onClick={() => setShowDeleteConfirm(false)}
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="error"
                            onClick={handleDeleteConfirm}
                        >
                            Supprimer
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
